/* global SharedArrayBuffer, Atomics */
import React, { useState, useEffect, useRef } from "react";
import Editor from "@monaco-editor/react";
import { 
  Play, Square, Trash2, Terminal, ChevronDown, Check,
  Wand2, Maximize2, Minimize2, Plus, Minus, Files, X, FileCode,
  Layout, FileText, Settings, Sparkles, HelpCircle, Brackets, AlignLeft, Map, Palette
} from "lucide-react";
import { FaPython, FaJsSquare } from "react-icons/fa";
import { AiOutlineFullscreen } from "react-icons/ai";
import { RxCrossCircled } from "react-icons/rx";
import ProjectFileManager from "./ProjectFileManager";
import { setupPythonLanguageService, updatePythonProjectSymbols } from "../../../utils/monaco/pythonLanguageService";
import { setupJavascriptLanguageService, updateJsProjectSymbols } from "../../../utils/monaco/javascriptLanguageService";

const DEFAULT_PYTHON_CODE = `# Practido Code Studio - Python 3
def welcome():
    print("Welcome to Practido Code Studio! 🐍")
    print("Write Python code here to create animations, math scripts, and interactive programs.")

welcome()

# Try a loop!
for i in range(1, 6):
    print(f"Step {i}: Practido Coding Rocks! 🚀")
`;

const DEFAULT_JS_CODE = `// Practido Code Studio - JavaScript ES6
function welcome() {
    console.log("Welcome to Practido Code Studio! ⚡");
    console.log("Write JavaScript code here to create interactive web scripts and algorithms.");
}

welcome();

// Try a loop!
for (let i = 1; i <= 5; i++) {
    console.log(\`Step \${i}: Practido Coding Rocks! 🚀\`);
}
`;

const LAYOUT_STORAGE_KEY = "practido_code_editor_layout_settings";
const SETTINGS_STORAGE_KEY = "practido_code_editor_feature_settings";

const getSavedLayoutSetting = (key, defaultValue) => {
  try {
    const raw = localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed[key] !== undefined && parsed[key] !== null) {
        return parsed[key];
      }
    }
  } catch (e) {
    console.warn("Failed to read editor layout setting from localStorage:", e);
  }
  return defaultValue;
};

const getSavedFeatureSetting = (key, defaultValue) => {
  try {
    const raw = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed[key] !== undefined && parsed[key] !== null) {
        return parsed[key];
      }
    }
  } catch (e) {
    console.warn("Failed to read editor feature setting from localStorage:", e);
  }
  return defaultValue;
};

const FormattedTerminalError = ({ text }) => {
  if (!text) return null;
  const rawLines = String(text).split("\n");
  let inTip = false;
  let isNextCodeLine = false;

  return (
    <div className="text-white font-normal space-y-0.5 leading-snug font-mono select-text text-xs">
      {rawLines.map((line, idx) => {
        if (!line && !inTip) {
          isNextCodeLine = false;
          return <div key={idx} className="h-1.5" />;
        }

        const tipMatch = line.match(/^(\s*(?:💡\s*)?Tip:\s*)(.*)$/);
        if (tipMatch) {
          inTip = true;
          return (
            <div key={idx} className="font-normal text-white">
              <span style={{ color: "#4ade80" }} className="text-emerald-400 font-medium">
                {tipMatch[1]}
              </span>
              <span className="text-white font-normal">
                {tipMatch[2]}
              </span>
            </div>
          );
        }

        if (inTip) {
          return (
            <div key={idx} className="text-white font-normal">
              {line}
            </div>
          );
        }

        const iconPrefix = idx === 0 ? (
          <RxCrossCircled className="w-3.5 h-3.5 text-red-500 inline-block mr-1.5 shrink-0 select-none align-text-bottom" />
        ) : null;

        if (line.trim().startsWith("^")) {
          isNextCodeLine = false;
          return (
            <div key={idx} className="text-red-400 font-mono">
              {iconPrefix}
              {line}
            </div>
          );
        }

        // Python File Line: File "main.py", line 1, in <module>
        const pyFileMatch = line.match(/^(\s*File\s+)("[^"]+"|'[^']+'|\S+)(,\s*)(line\s+\d+)(.*)$/);
        if (pyFileMatch) {
          isNextCodeLine = true;
          return (
            <div key={idx} className="text-white">
              {iconPrefix}
              <span>{pyFileMatch[1]}</span>
              <span className="text-red-400 font-medium">{pyFileMatch[2]}</span>
              <span>{pyFileMatch[3]}</span>
              <span className="text-red-400 font-medium">{pyFileMatch[4]}</span>
              <span>{pyFileMatch[5]}</span>
            </div>
          );
        }

        // Python Code line immediately following File line
        if (isNextCodeLine) {
          isNextCodeLine = false;
          return (
            <div key={idx} className="text-red-400 font-normal font-mono">
              {iconPrefix}
              {line}
            </div>
          );
        }

        // JS Arrow Location Line: --> main.js:1:13
        const jsArrowMatch = line.match(/^(\s*-->\s*)([^:\s]+)(:\d+(?::\d+)?)(.*)$/);
        if (jsArrowMatch) {
          return (
            <div key={idx} className="text-white">
              {iconPrefix}
              <span className="text-slate-400">{jsArrowMatch[1]}</span>
              <span className="text-red-400 font-medium">{jsArrowMatch[2]}</span>
              <span className="text-red-400 font-medium">{jsArrowMatch[3]}</span>
              <span>{jsArrowMatch[4]}</span>
            </div>
          );
        }

        // JS Code Line: 1 | console.log(score);
        const jsCodeMatch = line.match(/^(\s*\d+\s*\|\s*)(.*)$/);
        if (jsCodeMatch) {
          return (
            <div key={idx} className="text-white">
              {iconPrefix}
              <span className="text-slate-400">{jsCodeMatch[1]}</span>
              <span className="text-red-400 font-normal font-mono">{jsCodeMatch[2]}</span>
            </div>
          );
        }

        // Error Header Line: NameError: name 'score' is not defined
        const errMatch = line.match(/^([A-Z][a-zA-Z0-9_]*(?:Error|Exception|Warning)):\s*(.*)$/);
        if (errMatch) {
          return (
            <div key={idx} className="text-white">
              {iconPrefix}
              <span className="text-red-400 font-semibold">{errMatch[1]}</span>
              <span>: {errMatch[2]}</span>
            </div>
          );
        }

        return (
          <div key={idx} className="text-white font-normal">
            {iconPrefix}
            {line}
          </div>
        );
      })}
    </div>
  );
};

const PythonWorkspace = ({
  mode = "free_play",
  initialCode = null,
  selectedChild = null,
  readOnly = false,
  challengeData = null,
  onCodeChange = null,
  onGradeSubmit = null,
  timerSeconds = null,
  initialLanguage = "python",
  onRequestLanguageChange = null,
  onSaveSnippet = null,
}) => {
  const [selectedLanguage, setSelectedLanguage] = useState(
    initialLanguage === "javascript" ? "javascript" : "python"
  );

  // Independent Project State for Python Studio (In-Memory Persistence)
  const [pythonProject, setPythonProject] = useState(() => {
    let files = [
      {
        id: "main",
        name: "main.py",
        path: "main.py",
        content: DEFAULT_PYTHON_CODE,
        type: "file",
        parentId: null,
        isEntry: true,
      },
    ];
    let activeId = "main";
    if (initialLanguage === "python" || !initialLanguage) {
      if (initialCode && typeof initialCode === "object" && Array.isArray(initialCode.files)) {
        files = initialCode.files;
        const entry = files.find((f) => f.isEntry) || files[0];
        activeId = entry ? entry.id : "main";
      } else if (typeof initialCode === "string" && initialCode.trim()) {
        files[0].content = initialCode;
      }
    }
    return {
      files,
      activeFileId: activeId,
      openFileIds: files.map((f) => f.id),
      terminalLogs: [],
      installedPackages: [],
      status: "Ready",
    };
  });

  // Independent Project State for JavaScript Studio (In-Memory Persistence)
  const [jsProject, setJsProject] = useState(() => {
    let files = [
      {
        id: "main",
        name: "main.js",
        path: "main.js",
        content: DEFAULT_JS_CODE,
        type: "file",
        parentId: null,
        isEntry: true,
      },
    ];
    let activeId = "main";
    if (initialLanguage === "javascript") {
      if (initialCode && typeof initialCode === "object" && Array.isArray(initialCode.files)) {
        files = initialCode.files;
        const entry = files.find((f) => f.isEntry) || files[0];
        activeId = entry ? entry.id : "main";
      } else if (typeof initialCode === "string" && initialCode.trim()) {
        files[0].content = initialCode;
      }
    }
    return {
      files,
      activeFileId: activeId,
      openFileIds: files.map((f) => f.id),
      terminalLogs: [],
      status: "Ready",
    };
  });

  // Active Studio Context Helpers
  const isPython = selectedLanguage === "python";
  const currentProject = isPython ? pythonProject : jsProject;
  const projectFiles = currentProject.files;
  const activeFileId = currentProject.activeFileId;
  const terminalLogs = currentProject.terminalLogs || [];
  const installedPackages = isPython ? (pythonProject.installedPackages || []) : [];
  const status = currentProject.status || "Ready";

  const entryFile = projectFiles.find((f) => f.isEntry) || projectFiles[0];
  const rawOpenFileIds = currentProject.openFileIds || projectFiles.map((f) => f.id);
  // Ensure the entry file (e.g. main.py) is always present in openFileIds
  const openFileIds =
    entryFile && !rawOpenFileIds.includes(entryFile.id)
      ? [entryFile.id, ...rawOpenFileIds]
      : rawOpenFileIds.length > 0
      ? rawOpenFileIds
      : entryFile
      ? [entryFile.id]
      : [];

  const openFiles = projectFiles.filter((f) => openFileIds.includes(f.id));

  const activeFile =
    openFiles.find((f) => f.id === activeFileId) ||
    projectFiles.find((f) => f.id === activeFileId) ||
    openFiles[0] ||
    entryFile || {
      id: "main",
      name: isPython ? "main.py" : "main.js",
      path: isPython ? "main.py" : "main.js",
      content: isPython ? DEFAULT_PYTHON_CODE : DEFAULT_JS_CODE,
      isEntry: true,
    };

  const updateCurrentProject = (updater) => {
    if (isPython) {
      setPythonProject((prev) => (typeof updater === "function" ? updater(prev) : { ...prev, ...updater }));
    } else {
      setJsProject((prev) => (typeof updater === "function" ? updater(prev) : { ...prev, ...updater }));
    }
  };

  const setActiveFileId = (newId) => {
    updateCurrentProject({ activeFileId: newId });
  };

  const setTerminalLogs = (updater) => {
    updateCurrentProject((prev) => ({
      ...prev,
      terminalLogs: typeof updater === "function" ? updater(prev.terminalLogs || []) : updater,
    }));
  };

  const setStatus = (newStatus) => {
    updateCurrentProject((prev) => ({
      ...prev,
      status: typeof newStatus === "function" ? newStatus(prev.status) : newStatus,
    }));
  };

  const setInstalledPackages = (packages) => {
    if (isPython) {
      setPythonProject((prev) => ({
        ...prev,
        installedPackages: typeof packages === "function" ? packages(prev.installedPackages || []) : packages,
      }));
    }
  };

  // Layout State (Persisted in localStorage across sessions and studio switches)
  const [editorFontSize, setEditorFontSize] = useState(() =>
    getSavedLayoutSetting("fontSize", 14)
  );
  const [isFormatted, setIsFormatted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showConsolePanel, setShowConsolePanel] = useState(() =>
    getSavedLayoutSetting("showConsolePanel", true)
  );
  const [showFileExplorer, setShowFileExplorer] = useState(() =>
    getSavedLayoutSetting("showFileExplorer", true)
  );
  const [showMinimap, setShowMinimap] = useState(() =>
    getSavedLayoutSetting("showMinimap", true)
  );

  // Editor Feature Settings (Persisted in localStorage)
  const [enableSuggestions, setEnableSuggestions] = useState(() =>
    getSavedFeatureSetting("enableSuggestions", true)
  );
  const [enableParameterHints, setEnableParameterHints] = useState(() =>
    getSavedFeatureSetting("enableParameterHints", true)
  );
  const [enableAutoCloseBrackets, setEnableAutoCloseBrackets] = useState(() =>
    getSavedFeatureSetting("enableAutoCloseBrackets", true)
  );
  const [enableAutoFormat, setEnableAutoFormat] = useState(() =>
    getSavedFeatureSetting("enableAutoFormat", true)
  );
  const [enableBracketPairColorization, setEnableBracketPairColorization] = useState(() =>
    getSavedFeatureSetting("enableBracketPairColorization", true)
  );
  const [enableWordWrap, setEnableWordWrap] = useState(() =>
    getSavedFeatureSetting("enableWordWrap", true)
  );

  // Resizable Panels State (Persisted in localStorage)
  const [fileExplorerWidth, setFileExplorerWidth] = useState(() => {
    const val = getSavedLayoutSetting("fileExplorerWidth", 190);
    return Math.min(Math.max(Number(val) || 190, 130), 380);
  });
  const [consolePanelWidth, setConsolePanelWidth] = useState(() => {
    const val = getSavedLayoutSetting("consolePanelWidth", 320);
    return Math.min(Math.max(Number(val) || 320, 220), 800);
  });
  const isResizingExplorerRef = useRef(false);
  const isResizingConsoleRef = useRef(false);
  const containerRef = useRef(null);

  // Run Mode State (Approach 3: Split Run Button)
  const [runMode, setRunMode] = useState("project"); // "project" | "active_file"
  const [isRunMenuOpen, setIsRunMenuOpen] = useState(false);
  const runMenuRef = useRef(null);

  // Layout Dropdown State
  const [isLayoutMenuOpen, setIsLayoutMenuOpen] = useState(false);
  const layoutMenuRef = useRef(null);

  // Feature Settings Dropdown State
  const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);
  const settingsMenuRef = useRef(null);
  const monacoRef = useRef(null);

  // Execution and Worker State
  const [isRunning, setIsRunning] = useState(false);
  const [executionTime, setExecutionTime] = useState(null);
  const [isInstallingPackage, setIsInstallingPackage] = useState(false);

  // Interactive Input State (PictoBlox Inline UX)
  const [activeInputRequest, setActiveInputRequest] = useState(null); // { prompt: string } | null
  const [activeInputValue, setActiveInputValue] = useState("");
  const terminalInputRef = useRef(null);

  // Shared Memory Buffers for Atomics.wait sync input
  const sharedBufferRef = useRef(null);
  const controlBufferRef = useRef(null);
  const dataBufferRef = useRef(null);

  const ensureSharedBuffer = () => {
    if (typeof SharedArrayBuffer !== "undefined" && !sharedBufferRef.current) {
      try {
        const sab = new SharedArrayBuffer(65536);
        sharedBufferRef.current = sab;
        controlBufferRef.current = new Int32Array(sab, 0, 4);
        dataBufferRef.current = new Uint8Array(sab, 16, 65520);
      } catch (err) {
        console.warn("SharedArrayBuffer allocation failed:", err);
      }
    }
    return sharedBufferRef.current;
  };

  const workspaceContainerRef = useRef(null);
  const editorRef = useRef(null);
  const workerRef = useRef(null);
  const terminalEndRef = useRef(null);

  const onSaveSnippetRef = useRef(onSaveSnippet);
  const selectedLanguageRef = useRef(selectedLanguage);

  useEffect(() => {
    onSaveSnippetRef.current = onSaveSnippet;
  }, [onSaveSnippet]);

  useEffect(() => {
    selectedLanguageRef.current = selectedLanguage;
  }, [selectedLanguage]);

  // Persist layout settings to localStorage on any change
  useEffect(() => {
    try {
      const layoutData = {
        fileExplorerWidth,
        consolePanelWidth,
        showFileExplorer,
        showConsolePanel,
        showMinimap,
        fontSize: editorFontSize,
      };
      localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(layoutData));
    } catch (e) {
      console.warn("Error saving editor layout settings to localStorage:", e);
    }
  }, [fileExplorerWidth, consolePanelWidth, showFileExplorer, showConsolePanel, showMinimap, editorFontSize]);

  // Persist feature settings to localStorage on any change
  useEffect(() => {
    try {
      const featureData = {
        enableSuggestions,
        enableParameterHints,
        enableAutoCloseBrackets,
        enableAutoFormat,
        enableBracketPairColorization,
        enableWordWrap,
      };
      localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(featureData));
    } catch (e) {
      console.warn("Error saving editor feature settings to localStorage:", e);
    }
  }, [
    enableSuggestions,
    enableParameterHints,
    enableAutoCloseBrackets,
    enableAutoFormat,
    enableBracketPairColorization,
    enableWordWrap,
  ]);

  // Dynamically update Python and JS multi-file symbols for autocomplete
  useEffect(() => {
    updatePythonProjectSymbols(projectFiles);
    if (monacoRef.current) {
      updateJsProjectSymbols(monacoRef.current, projectFiles);
    }
  }, [projectFiles]);

  // Broadcast active context when studio changes
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("workspace-active-context-changed", {
        detail: {
          tab: "editor",
          language: selectedLanguage,
          environment: selectedLanguage,
        },
      })
    );
  }, [selectedLanguage]);

  // Handle external updates to initialCode
  useEffect(() => {
    if (!initialCode) return;
    if (selectedLanguage === "python") {
      setPythonProject((prev) => {
        let files = prev.files;
        let activeId = prev.activeFileId;
        if (typeof initialCode === "object" && Array.isArray(initialCode.files)) {
          files = initialCode.files;
          const entry = files.find((f) => f.isEntry) || files[0];
          activeId = entry ? entry.id : "main";
        } else if (Array.isArray(initialCode)) {
          files = initialCode;
          const entry = files.find((f) => f.isEntry) || files[0];
          activeId = entry ? entry.id : "main";
        } else if (typeof initialCode === "string") {
          files = prev.files.map((f) => (f.isEntry ? { ...f, content: initialCode } : f));
        }
        return {
          ...prev,
          files,
          activeFileId: activeId,
          openFileIds: files.map((f) => f.id),
        };
      });
    } else {
      setJsProject((prev) => {
        let files = prev.files;
        let activeId = prev.activeFileId;
        if (typeof initialCode === "object" && Array.isArray(initialCode.files)) {
          files = initialCode.files;
          const entry = files.find((f) => f.isEntry) || files[0];
          activeId = entry ? entry.id : "main";
        } else if (Array.isArray(initialCode)) {
          files = initialCode;
          const entry = files.find((f) => f.isEntry) || files[0];
          activeId = entry ? entry.id : "main";
        } else if (typeof initialCode === "string") {
          files = prev.files.map((f) => (f.isEntry ? { ...f, content: initialCode } : f));
        }
        return {
          ...prev,
          files,
          activeFileId: activeId,
          openFileIds: files.map((f) => f.id),
        };
      });
    }
  }, [initialCode]);

  // Sync initialLanguage prop
  useEffect(() => {
    if (initialLanguage && initialLanguage !== selectedLanguage) {
      setSelectedLanguage(initialLanguage);
    }
  }, [initialLanguage]);

  // Notify parent on code change
  useEffect(() => {
    if (onCodeChange) {
      const entryFile = projectFiles.find((f) => f.isEntry) || projectFiles[0];
      onCodeChange(entryFile ? entryFile.content : activeFile.content, selectedLanguage, projectFiles);
    }
  }, [projectFiles, selectedLanguage, onCodeChange]);

  // Backpack snippet insertion listener
  useEffect(() => {
    const handleInsertBackpackSnippet = (event) => {
      const snippet = event.detail;
      if (!snippet) return;
      const snippetText = snippet.code !== undefined && snippet.code !== null ? snippet.code : "";
      if (!snippetText) return;

      const editor = editorRef.current;
      if (editor) {
        const selection = editor.getSelection();
        if (selection) {
          editor.executeEdits("backpack", [
            {
              range: selection,
              text: snippetText,
              forceMoveMarkers: true,
            },
          ]);
          editor.focus();
        } else {
          const currentVal = editor.getValue();
          const newVal = currentVal ? `${currentVal}\n\n${snippetText}` : snippetText;
          editor.setValue(newVal);
          editor.focus();
        }
        const updated = editor.getValue();
        handleActiveFileCodeChange(updated);
      } else {
        const next = activeFile.content ? `${activeFile.content}\n\n${snippetText}` : snippetText;
        handleActiveFileCodeChange(next);
      }
    };

    window.addEventListener("insert-backpack-code-snippet", handleInsertBackpackSnippet);
    return () => window.removeEventListener("insert-backpack-code-snippet", handleInsertBackpackSnippet);
  }, [activeFile]);

  // Fullscreen toggle
  const toggleFullscreen = () => {
    if (!workspaceContainerRef.current) return;
    if (!document.fullscreenElement) {
      workspaceContainerRef.current.requestFullscreen().catch((err) => {
        console.error("Failed to enter fullscreen mode:", err);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const handleToggleConsole = () => {
    setShowConsolePanel((prev) => !prev);
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (editorRef.current) {
          try {
            editorRef.current.layout();
          } catch (e) {}
        }
        window.dispatchEvent(new Event("resize"));
      }, 60);
    });
  };

  // Close menus on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (runMenuRef.current && !runMenuRef.current.contains(e.target)) {
        setIsRunMenuOpen(false);
      }
      if (layoutMenuRef.current && !layoutMenuRef.current.contains(e.target)) {
        setIsLayoutMenuOpen(false);
      }
      if (settingsMenuRef.current && !settingsMenuRef.current.contains(e.target)) {
        setIsSettingsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Explorer Resizing
  const handleExplorerMouseDown = (e) => {
    e.preventDefault();
    isResizingExplorerRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const startX = e.clientX;
    const startWidth = fileExplorerWidth;

    const handleMouseMove = (moveEvent) => {
      if (!isResizingExplorerRef.current) return;
      const delta = moveEvent.clientX - startX;
      const newWidth = Math.min(Math.max(startWidth + delta, 130), 380);
      setFileExplorerWidth(newWidth);
      if (editorRef.current) {
        try {
          editorRef.current.layout();
        } catch (err) {}
      }
    };

    const handleMouseUp = () => {
      isResizingExplorerRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      if (editorRef.current) {
        try {
          editorRef.current.layout();
        } catch (err) {}
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  // Console Resizing
  const handleConsoleMouseDown = (e) => {
    e.preventDefault();
    isResizingConsoleRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const startX = e.clientX;
    const startWidth = consolePanelWidth;

    const handleMouseMove = (moveEvent) => {
      if (!isResizingConsoleRef.current) return;
      const delta = startX - moveEvent.clientX;
      const maxW = containerRef.current
        ? Math.max(260, containerRef.current.clientWidth - 320)
        : 700;
      const newWidth = Math.min(Math.max(startWidth + delta, 220), maxW);
      setConsolePanelWidth(newWidth);
      if (editorRef.current) {
        try {
          editorRef.current.layout();
        } catch (err) {}
      }
    };

    const handleMouseUp = () => {
      isResizingConsoleRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      if (editorRef.current) {
        try {
          editorRef.current.layout();
        } catch (err) {}
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  // Code Formatting
  const handleFormatCode = () => {
    if (!activeFile || !activeFile.content) return;
    let formatted = activeFile.content;
    if (selectedLanguage === "javascript") {
      const lines = formatted.split("\n");
      let indentLevel = 0;
      const formattedLines = lines.map((line) => {
        let trimmed = line.trim();
        if (!trimmed) return "";
        if (trimmed.startsWith("}")) {
          indentLevel = Math.max(0, indentLevel - 1);
        }
        const indentStr = "    ".repeat(indentLevel);
        if (trimmed.endsWith("{")) {
          indentLevel++;
        }
        return `${indentStr}${trimmed}`;
      });
      formatted = formattedLines.join("\n");
    } else {
      const lines = formatted.split("\n");
      const formattedLines = lines.map((line) => {
        const leadingSpaces = line.match(/^\s*/)[0].length;
        const normalizedIndent = " ".repeat(Math.round(leadingSpaces / 4) * 4);
        return `${normalizedIndent}${line.trim()}`;
      });
      formatted = formattedLines.join("\n");
    }

    handleActiveFileCodeChange(formatted);
    setIsFormatted(true);
    setTimeout(() => setIsFormatted(false), 2000);
  };

  // File Manipulations
  const handleActiveFileCodeChange = (newVal) => {
    const value = newVal || "";
    const updatedFiles = projectFiles.map((f) =>
      f.id === activeFile.id ? { ...f, content: value } : f
    );
    updateCurrentProject({ files: updatedFiles });
    if (onCodeChange) {
      const entryFile = updatedFiles.find((f) => f.isEntry) || updatedFiles[0];
      onCodeChange(entryFile ? entryFile.content : value, selectedLanguageRef.current, updatedFiles);
    }
  };

  const handleSelectFile = (fileId) => {
    const currentOpen = currentProject.openFileIds || projectFiles.map((f) => f.id);
    const updatedOpen = currentOpen.includes(fileId) ? currentOpen : [...currentOpen, fileId];
    updateCurrentProject({
      openFileIds: updatedOpen,
      activeFileId: fileId,
    });
  };

  const handleCloseFileTab = (fileId) => {
    const currentOpen = currentProject.openFileIds || projectFiles.map((f) => f.id);
    const updatedOpen = currentOpen.filter((id) => id !== fileId);

    // If the closed tab was the active tab, pick a new active tab
    let nextActiveId = activeFileId;
    if (activeFileId === fileId) {
      const remainingOpenFiles = projectFiles.filter((f) => updatedOpen.includes(f.id));
      if (remainingOpenFiles.length > 0) {
        const prevIndex = openFiles.findIndex((f) => f.id === fileId);
        const candidate =
          remainingOpenFiles[Math.max(0, prevIndex - 1)] || remainingOpenFiles[0];
        nextActiveId = candidate.id;
      } else {
        const entry = projectFiles.find((f) => f.isEntry) || projectFiles[0];
        nextActiveId = entry ? entry.id : "main";
        if (entry && !updatedOpen.includes(entry.id)) {
          updatedOpen.push(entry.id);
        }
      }
    }

    updateCurrentProject({
      openFileIds: updatedOpen,
      activeFileId: nextActiveId,
    });
  };

  const handleCreateFile = ({ name, content, type }) => {
    if (!name || !name.trim()) return;
    const cleanName = name.trim();
    if (projectFiles.some((f) => f.name.toLowerCase() === cleanName.toLowerCase())) {
      alert(`A file named "${cleanName}" already exists in the project.`);
      return;
    }

    const newId = `file_${Date.now()}`;
    const newFile = {
      id: newId,
      name: cleanName,
      content: content || "",
      isEntry: false,
      type: type || "file",
      path: cleanName,
    };
    const updated = [...projectFiles, newFile];
    const currentOpen = currentProject.openFileIds || projectFiles.map((f) => f.id);
    const updatedOpen = [...currentOpen, newId];

    updateCurrentProject({
      files: updated,
      openFileIds: updatedOpen,
      activeFileId: type !== "folder" ? newId : activeFileId,
    });
    if (onCodeChange) {
      const entryFile = updated.find((f) => f.isEntry) || updated[0];
      onCodeChange(entryFile ? entryFile.content : activeFile.content, selectedLanguageRef.current, updated);
    }
  };

  const handleDeleteFile = (fileId) => {
    const file = projectFiles.find((f) => f.id === fileId);
    if (!file || file.isEntry) return;

    if (monacoRef.current) {
      try {
        const modelUri = monacoRef.current.Uri.parse(`${selectedLanguage}/${file.id}/${file.name}`);
        const model = monacoRef.current.editor.getModel(modelUri);
        if (model) {
          model.dispose();
        }
      } catch (e) {
        console.warn("Failed to dispose model for deleted file:", e);
      }
    }

    const updated = projectFiles.filter((f) => f.id !== fileId);
    const currentOpen = currentProject.openFileIds || projectFiles.map((f) => f.id);
    const updatedOpen = currentOpen.filter((id) => id !== fileId);

    let nextActiveId = activeFileId;
    if (activeFileId === fileId) {
      const remainingOpenFiles = updated.filter((f) => updatedOpen.includes(f.id));
      if (remainingOpenFiles.length > 0) {
        nextActiveId = remainingOpenFiles[0].id;
      } else {
        const remaining = updated[0];
        nextActiveId = remaining ? remaining.id : "main";
      }
    }
    updateCurrentProject({
      files: updated,
      openFileIds: updatedOpen,
      activeFileId: nextActiveId,
    });
    if (onCodeChange) {
      const entryFile = updated.find((f) => f.isEntry) || updated[0];
      onCodeChange(entryFile ? entryFile.content : "", selectedLanguageRef.current, updated);
    }
  };

  const handleRenameFile = (fileId, newName) => {
    const oldFile = projectFiles.find((f) => f.id === fileId);
    if (oldFile && monacoRef.current) {
      try {
        const oldUri = monacoRef.current.Uri.parse(`${selectedLanguage}/${oldFile.id}/${oldFile.name}`);
        const model = monacoRef.current.editor.getModel(oldUri);
        if (model) {
          model.dispose();
        }
      } catch (e) {}
    }
    const updated = projectFiles.map((f) =>
      f.id === fileId ? { ...f, name: newName, path: newName } : f
    );
    updateCurrentProject({ files: updated });
    if (onCodeChange) {
      const entryFile = updated.find((f) => f.isEntry) || updated[0];
      onCodeChange(entryFile ? entryFile.content : activeFile.content, selectedLanguageRef.current, updated);
    }
  };

  const handleQuickAddFile = () => {
    const ext = isPython ? ".py" : ".js";
    const defaultBase = "module";
    const count = projectFiles.filter((f) => f.name.endsWith(ext)).length;
    const suggestedName = `${defaultBase}${count}`;
    const fileName = window.prompt(`Enter new ${isPython ? "Python" : "JavaScript"} file name (eg. : module):`, suggestedName);
    if (fileName && fileName.trim()) {
      let finalName = fileName.trim();
      if (!finalName.includes(".")) {
        finalName = `${finalName}${ext}`;
      }
      handleCreateFile({
        name: finalName,
        content: isPython ? `# ${finalName}\n` : `// ${finalName}\n`,
        type: "file",
      });
    }
  };

  const handleDownloadSingleFile = (file) => {
    const blob = new Blob([file.content || ""], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Studio Switcher: Python <-> JavaScript
  const handleStudioSwitch = (newLang) => {
    if (newLang === selectedLanguage) return;
    setSelectedLanguage(newLang);
    if (onRequestLanguageChange) {
      onRequestLanguageChange(newLang);
    }
  };

  // Web Worker Management
  const initWorker = (lang = selectedLanguage) => {
    if (workerRef.current) {
      workerRef.current.terminate();
    }

    setActiveInputRequest(null);
    setActiveInputValue("");

    const sab = ensureSharedBuffer();
    if (controlBufferRef.current) {
      Atomics.store(controlBufferRef.current, 0, 0);
      Atomics.store(controlBufferRef.current, 1, 0);
      Atomics.store(controlBufferRef.current, 2, 0);
    }

    setStatus("Initializing Engine...");
    const workerScript = lang === "javascript" ? "/js-worker.js" : "/pyodide-worker.js";
    const worker = new Worker(workerScript);
    workerRef.current = worker;

    worker.onmessage = (e) => {
      const { type, text, duration, time } = e.data;
      if (type === "STATUS") {
        setStatus(text);
      } else if (type === "READY") {
        setStatus("Ready");
      } else if (type === "STDOUT" || type === "LOG") {
        setTerminalLogs((prev) => [...prev, { type: "stdout", text }]);
      } else if (type === "STDERR") {
        setTerminalLogs((prev) => [...prev, { type: "stderr", text }]);
      } else if (type === "STDIN_REQUEST") {
        setStatus("Waiting for input...");
        setActiveInputRequest({ prompt: e.data.prompt || "" });
        setActiveInputValue("");
        setTimeout(() => {
          if (terminalInputRef.current) {
            terminalInputRef.current.focus();
          }
        }, 40);
      } else if (type === "DONE") {
        setActiveInputRequest(null);
        setActiveInputValue("");
        setStatus("Ready");
        setIsRunning(false);
        if (duration !== undefined || time !== undefined) {
          setExecutionTime(duration || time);
        }
      } else if (type === "INSTALLED_PACKAGES") {
        if (Array.isArray(e.data.packages)) {
          setInstalledPackages(e.data.packages);
        }
      } else if (type === "PACKAGE_INSTALLED_SUCCESS") {
        setIsInstallingPackage(false);
      } else if (type === "PACKAGE_INSTALL_ERROR") {
        setIsInstallingPackage(false);
      } else if (type === "ERROR") {
        setActiveInputRequest(null);
        setActiveInputValue("");
        setStatus("Error");
        setIsRunning(false);
        setIsInstallingPackage(false);
        setTerminalLogs((prev) => [...prev, { type: "error", text }]);
      }
    };

    worker.onerror = (err) => {
      setActiveInputRequest(null);
      setActiveInputValue("");
      setStatus("Error");
      setIsRunning(false);
      setTerminalLogs((prev) => [
        ...prev,
        { type: "error", text: `Worker Exception: ${err.message}` },
      ]);
    };

    if (sab) {
      worker.postMessage({ type: "INIT_SHARED_BUFFER", sharedBuffer: sab });
    }
    worker.postMessage({ type: "INIT" });

    setTimeout(() => {
      setStatus((curr) => (curr === "Initializing Engine..." || curr.includes("Initializing") ? "Ready" : curr));
    }, 1200);
  };

  useEffect(() => {
    initWorker(selectedLanguage);
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
      }
    };
  }, [selectedLanguage]);

  useEffect(() => {
    if (terminalEndRef.current) {
      terminalEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [terminalLogs, activeInputRequest]);

  const sendInputToWorker = (val) => {
    if (!controlBufferRef.current || !dataBufferRef.current) {
      console.warn("SharedArrayBuffer not available to send input");
      return;
    }
    const encoder = new TextEncoder();
    const bytes = encoder.encode(val);
    const maxLen = dataBufferRef.current.length;
    const bytesToStore = bytes.slice(0, maxLen);

    dataBufferRef.current.set(bytesToStore, 0);
    Atomics.store(controlBufferRef.current, 1, bytesToStore.length);
    Atomics.store(controlBufferRef.current, 0, 2); // 2 = INPUT_READY
    Atomics.notify(controlBufferRef.current, 0, 1);
  };

  const handleCommitTerminalInput = () => {
    if (!activeInputRequest) return;
    const val = activeInputValue;
    const promptText = activeInputRequest.prompt || "";
    const committedText = `${promptText}${val}`;

    setTerminalLogs((prev) => [...prev, { type: "stdout", text: committedText }]);
    setActiveInputRequest(null);
    setActiveInputValue("");
    setStatus("Running...");

    sendInputToWorker(val);
  };

  const handleTerminalInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleCommitTerminalInput();
    } else if (e.key === "Escape" || (e.ctrlKey && e.key.toLowerCase() === "c")) {
      e.preventDefault();
      handleStopExecution();
    }
  };

  const handleInstallPackage = (packageName) => {
    if (!workerRef.current || !packageName) return;
    setIsInstallingPackage(true);
    workerRef.current.postMessage({
      type: "INSTALL_PACKAGE",
      packageName,
    });
  };

  const handleRunCode = (modeOverride = null) => {
    if (!workerRef.current || isRunning) return;

    if (!showConsolePanel) {
      setShowConsolePanel(true);
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (editorRef.current) {
            try {
              editorRef.current.layout();
            } catch (e) {}
          }
          window.dispatchEvent(new Event("resize"));
        }, 60);
      });
    }

    const currentMode = modeOverride || runMode;
    const entryFile = projectFiles.find((f) => f.isEntry) || projectFiles[0];
    const targetFile = currentMode === "active_file" ? (activeFile || entryFile) : entryFile;

    setActiveInputRequest(null);
    setActiveInputValue("");
    if (controlBufferRef.current) {
      Atomics.store(controlBufferRef.current, 0, 0);
      Atomics.store(controlBufferRef.current, 1, 0);
      Atomics.store(controlBufferRef.current, 2, 0);
    }

    setTerminalLogs([]);
    setIsRunning(true);
    setStatus("Running...");
    setExecutionTime(null);
    setTerminalLogs([
      {
        type: "system",
        text: currentMode === "active_file"
          ? `🚀 Running ${targetFile?.name || "active file"} (${selectedLanguage === "javascript" ? "JavaScript ES6" : "Python 3"})...`
          : `🚀 Running ${selectedLanguage === "javascript" ? "JavaScript ES6" : "Python 3"} project (${projectFiles.length} file${projectFiles.length > 1 ? "s" : ""})...`,
      },
    ]);

    workerRef.current.postMessage({
      type: "RUN_FILES",
      files: projectFiles,
      entryFile: targetFile?.path || targetFile?.name,
      code: targetFile?.content,
    });
  };

  const handleStopExecution = () => {
    if (controlBufferRef.current) {
      Atomics.store(controlBufferRef.current, 2, 2); // SIGINT
      Atomics.store(controlBufferRef.current, 0, -1); // INTERRUPTED
      Atomics.notify(controlBufferRef.current, 0, 1);
    }
    setActiveInputRequest(null);
    setActiveInputValue("");
    if (workerRef.current) {
      workerRef.current.terminate();
      setIsRunning(false);
      setStatus("Terminated");
      setTerminalLogs((prev) => [
        ...prev,
        { type: "stderr", text: "\n⚠️ Program execution terminated by user." },
      ]);
      initWorker(selectedLanguage);
    }
  };

  const handleClearTerminal = () => {
    setTerminalLogs([]);
  };

  const handleSaveCodeSnippet = () => {
    const editor = editorRef.current;
    let snippetCode = "";
    if (editor) {
      const selection = editor.getSelection();
      if (selection && !selection.isEmpty()) {
        snippetCode = editor.getModel().getValueInRange(selection);
      } else {
        snippetCode = editor.getValue();
      }
    } else {
      snippetCode = activeFile.content;
    }

    if (!snippetCode || !snippetCode.trim()) {
      return;
    }

    const lines = snippetCode.trim().split("\n").filter((l) => l.trim().length > 0);
    let defaultName = "Code Snippet";
    if (lines.length > 0) {
      const cleanFirstLine = lines[0]
        .replace(/^(#|\/\/|\/\*|\*|\s)+/, "")
        .replace(/^(def|function|const|let|var|class|import|from)\s+/, "")
        .trim();
      if (cleanFirstLine) {
        defaultName = cleanFirstLine.slice(0, 35);
      }
    }

    if (onSaveSnippetRef.current) {
      onSaveSnippetRef.current({
        code: snippetCode,
        language: selectedLanguageRef.current,
        environment: selectedLanguageRef.current,
        type: "code_snippet",
        defaultName,
      });
    }
  };

  const getStatusColorConfig = (statusStr, isRunningState) => {
    if (statusStr.includes("Waiting")) {
      return { dotColor: "#38bdf8", textColor: "#38bdf8", dotAnimation: "animate-pulse" };
    }
    if (isRunningState || statusStr === "Running...") {
      return { dotColor: "#f59e0b", textColor: "#fcd34d", dotAnimation: "animate-ping" };
    }
    if (statusStr === "Ready") {
      return { dotColor: "#34d399", textColor: "#34d399", dotAnimation: "" };
    }
    if (statusStr.includes("Error")) {
      return { dotColor: "#ef4444", textColor: "#ef4444", dotAnimation: "" };
    }
    if (statusStr.includes("Initializing") || statusStr.includes("Switching")) {
      return { dotColor: "#38bdf8", textColor: "#38bdf8", dotAnimation: "animate-pulse" };
    }
    if (statusStr.includes("Terminated") || statusStr.includes("Stopped")) {
      return { dotColor: "#fb923c", textColor: "#fb923c", dotAnimation: "" };
    }
    return { dotColor: "#94a3b8", textColor: "#cbd5e1", dotAnimation: "" };
  };

  const statusColor = getStatusColorConfig(status, isRunning);

  const getTabFileIcon = (file) => {
    if (file.name.endsWith(".js") || file.name.endsWith(".jsx")) {
      return <FaJsSquare className="w-3.5 h-3.5 text-[#F7DF1E] shrink-0" />;
    }
    if (file.name.endsWith(".py")) {
      return <FaPython className="w-3.5 h-3.5 text-[#38bdf8] shrink-0" />;
    }
    if (file.name.endsWith(".txt") || file.name.endsWith(".md")) {
      return <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />;
    }
    return <FileCode className="w-3.5 h-3.5 text-purple-400 shrink-0" />;
  };

  return (
    <div ref={workspaceContainerRef} className="w-full h-full bg-[#181818] flex flex-col overflow-hidden text-white font-sans relative">
      {/* ========================================================================= */}
      {/* TOP BAR (BAR 1): TABS (LEFT), RUN BUTTON (CENTER), TOOLS (RIGHT)           */}
      {/* ========================================================================= */}
      <div className="bg-[#21262d] border-b border-[#30363d] px-4 py-1.5 flex items-center justify-between gap-3 flex-shrink-0 z-10">
        
        {/* Left Side: Independent Studios Switcher Tabs (Styled as requested in Image 1) */}
        <div className="flex items-center gap-1 bg-[#161b22]/80 p-1 rounded-xl">
          <button
            type="button"
            onClick={() => handleStudioSwitch("python")}
            className={`px-3.5 py-1.5 rounded-lg text-xs flex items-center gap-2 transition-all cursor-pointer ${
              selectedLanguage === "python"
                ? "bg-purple-600 text-white font-extrabold shadow-sm"
                : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/40 font-semibold"
            }`}
          >
            <FaPython className="w-4 h-4 text-[#38bdf8]" />
            <span>Python</span>
          </button>

          <button
            type="button"
            onClick={() => handleStudioSwitch("javascript")}
            className={`px-3.5 py-1.5 rounded-lg text-xs flex items-center gap-2 transition-all cursor-pointer ${
              selectedLanguage === "javascript"
                ? "bg-purple-600 text-white font-extrabold shadow-sm"
                : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/40 font-semibold"
            }`}
          >
            <FaJsSquare className="w-4 h-4 text-[#F7DF1E]" />
            <span>JavaScript</span>
          </button>
        </div>

        {/* Center: Run Project & Stop Execution Buttons */}
        <div className="flex items-center justify-center gap-2">
          {/* Split Run Button with Run Mode Dropdown */}
          <div ref={runMenuRef} className="relative inline-flex items-stretch rounded-xl shadow-md">
            <button
              type="button"
              onClick={() => handleRunCode(runMode)}
              disabled={readOnly || isRunning}
              style={{ backgroundColor: isRunning ? "#065f46" : "#16a34a", color: "#ffffff" }}
              className={`px-4 py-1.5 active:scale-95 text-white disabled:opacity-60 disabled:cursor-not-allowed rounded-l-xl text-xs font-extrabold flex items-center gap-2 transition cursor-pointer border-0 ${
                !isRunning ? "hover:bg-emerald-600" : ""
              }`}
            >
              <Play className="w-3.5 h-3.5 fill-current text-white" />
              <span>
                {runMode === "active_file"
                  ? `Run ${activeFile?.name || "File"}`
                  : "Run Project"}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setIsRunMenuOpen((prev) => !prev)}
              disabled={readOnly || isRunning}
              style={{ backgroundColor: isRunning ? "#064e3b" : "#15803d", color: "#ffffff" }}
              className={`px-2 py-1.5 active:scale-95 text-white disabled:opacity-60 disabled:cursor-not-allowed rounded-r-xl border-l border-emerald-500/40 flex items-center justify-center transition cursor-pointer ${
                !isRunning ? "hover:bg-emerald-700" : ""
              }`}
              title="Run Options (Project or Active File)"
            >
              <ChevronDown className={`w-3.5 h-3.5 text-white transition-transform duration-150 ${isRunMenuOpen ? "rotate-180" : ""}`} />
            </button>

            {isRunMenuOpen && (
              <div
                className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 w-60 bg-[#161b22] rounded-xl p-1.5 z-50 space-y-1 code-workspace-dark-menu"
                style={{
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  outline: "none",
                  boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.8), 0 8px 10px -6px rgba(0, 0, 0, 0.8)",
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setRunMode("project");
                    setIsRunMenuOpen(false);
                    handleRunCode("project");
                  }}
                  className={`w-full text-left px-2.5 py-2 rounded-lg flex items-center justify-between text-xs transition cursor-pointer ${
                    runMode === "project"
                      ? "bg-purple-600/30 text-white font-bold"
                      : "text-slate-300 hover:bg-slate-800 hover:text-white"
                  }`}
                  style={{ border: "none", outline: "none" }}
                >
                  <div className="flex items-center gap-2 truncate">
                    <Play className="w-3.5 h-3.5 text-emerald-400 shrink-0 fill-current" />
                    <div className="truncate">
                      <div className="font-extrabold text-white">Run Project</div>
                      <div className="text-[10px] text-slate-400 truncate">
                        Entry: {projectFiles.find((f) => f.isEntry)?.name || (isPython ? "main.py" : "main.js")}
                      </div>
                    </div>
                  </div>
                  {runMode === "project" && <Check className="w-3.5 h-3.5 text-purple-400 shrink-0" />}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setRunMode("active_file");
                    setIsRunMenuOpen(false);
                    handleRunCode("active_file");
                  }}
                  className={`w-full text-left px-2.5 py-2 rounded-lg flex items-center justify-between text-xs transition cursor-pointer ${
                    runMode === "active_file"
                      ? "bg-purple-600/30 text-white font-bold"
                      : "text-slate-300 hover:bg-slate-800 hover:text-white"
                  }`}
                  style={{ border: "none", outline: "none" }}
                >
                  <div className="flex items-center gap-2 truncate">
                    <Play className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                    <div className="truncate">
                      <div className="font-extrabold text-white">Run Active File</div>
                      <div className="text-[10px] text-slate-400 truncate">
                        {activeFile?.name || "Current File"}
                      </div>
                    </div>
                  </div>
                  {runMode === "active_file" && <Check className="w-3.5 h-3.5 text-purple-400 shrink-0" />}
                </button>
              </div>
            )}
          </div>

          {/* Stop Execution Button (Always visible near the Run button) */}
          <button
            type="button"
            onClick={handleStopExecution}
            disabled={!isRunning}
            style={{
              backgroundColor: isRunning ? "#dc2626" : "#2d1a1a",
              color: isRunning ? "#ffffff" : "#7f1d1d",
            }}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-extrabold shadow-md flex items-center gap-1.5 transition border-0 ${
              isRunning
                ? "hover:bg-red-700 active:scale-95 cursor-pointer shadow-red-900/30"
                : "cursor-not-allowed opacity-50"
            }`}
            title={isRunning ? "Stop Program Execution" : "No program is currently running"}
          >
            <Square className="w-3.5 h-3.5 fill-current" />
            <span>Stop</span>
          </button>
        </div>

        {/* Right Side: Format Code, Font Size, Layout Dropdown, Mode Pill */}
        <div className="flex items-center gap-2.5">
          {/* Format Code Button */}
          <button
            type="button"
            onClick={handleFormatCode}
            title={isFormatted ? "Code Formatted!" : "Format Code (Auto-Prettify)"}
            className="w-8 h-8 bg-[#161b22]/90 hover:bg-slate-800 text-slate-300 hover:text-purple-300 rounded-lg flex items-center justify-center transition cursor-pointer border-0 shadow-xs"
          >
            {isFormatted ? (
              <Check className="w-4.5 h-4.5 text-emerald-400" />
            ) : (
              <Wand2 className="w-4.5 h-4.5 text-purple-400" />
            )}
          </button>

          {/* Font Size Adjuster Controls */}
          <div className="h-8 flex items-center bg-[#161b22]/90 rounded-lg px-1 text-xs shadow-xs border-0">
            <button
              type="button"
              onClick={() => setEditorFontSize((prev) => Math.max(11, prev - 1))}
              disabled={editorFontSize <= 11}
              className="w-6 h-6 flex items-center justify-center hover:bg-slate-800 text-slate-300 disabled:opacity-30 rounded-md transition cursor-pointer"
              title="Decrease Font Size"
            >
              <Minus className="w-3.5 h-3.5" />
            </button>
            <span className="px-1 text-xs font-extrabold font-mono text-amber-300 min-w-[34px] text-center">
              {editorFontSize}px
            </span>
            <button
              type="button"
              onClick={() => setEditorFontSize((prev) => Math.min(24, prev + 1))}
              disabled={editorFontSize >= 24}
              className="w-6 h-6 flex items-center justify-center hover:bg-slate-800 text-slate-300 disabled:opacity-30 rounded-md transition cursor-pointer"
              title="Increase Font Size"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Layout Toggle Dropdown Button */}
          <div ref={layoutMenuRef} className="relative">
            <button
              type="button"
              onClick={() => setIsLayoutMenuOpen((prev) => !prev)}
              title="Workspace Layout Options"
              className={`w-8 h-8 rounded-lg text-xs transition cursor-pointer flex items-center justify-center border-0 shadow-xs ${
                isLayoutMenuOpen
                  ? "bg-purple-600 text-white"
                  : "bg-[#161b22]/90 hover:bg-slate-800 text-slate-300 hover:text-purple-300"
              }`}
            >
              <Layout className="w-4.5 h-4.5" />
            </button>

            {isLayoutMenuOpen && (
              <div
                className="absolute right-0 top-full mt-1.5 w-56 bg-[#161b22] rounded-xl p-1.5 z-50 space-y-1 code-workspace-dark-menu"
                style={{
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  outline: "none",
                  boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.8), 0 8px 10px -6px rgba(0, 0, 0, 0.8)",
                }}
              >
                <div className="px-2.5 py-1 text-[11px] font-bold text-slate-300 select-none">
                  Layout Settings
                </div>
                <div
                  className="my-1 divider-line"
                  style={{ height: "1px", backgroundColor: "rgba(255, 255, 255, 0.08)", border: "none" }}
                />

                <button
                  type="button"
                  onClick={() => {
                    setShowFileExplorer((prev) => !prev);
                  }}
                  className="w-full text-left px-2.5 py-2 rounded-lg flex items-center justify-between text-xs text-slate-200 hover:bg-slate-800 hover:text-white transition cursor-pointer"
                  style={{ border: "none", outline: "none" }}
                >
                  <div className="flex items-center gap-2">
                    <Files className="w-4 h-4 text-purple-400" />
                    <span>Project Files Panel</span>
                  </div>
                  {showFileExplorer && (
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 stroke-[2.5]" />
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    handleToggleConsole();
                  }}
                  className="w-full text-left px-2.5 py-2 rounded-lg flex items-center justify-between text-xs text-slate-200 hover:bg-slate-800 hover:text-white transition cursor-pointer"
                  style={{ border: "none", outline: "none" }}
                >
                  <div className="flex items-center gap-2">
                    <Terminal className="w-4 h-4 text-indigo-400" />
                    <span>Bottom Panel</span>
                  </div>
                  {showConsolePanel && (
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 stroke-[2.5]" />
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowMinimap((prev) => !prev);
                  }}
                  className="w-full text-left px-2.5 py-2 rounded-lg flex items-center justify-between text-xs text-slate-200 hover:bg-slate-800 hover:text-white transition cursor-pointer"
                  style={{ border: "none", outline: "none" }}
                >
                  <div className="flex items-center gap-2">
                    <Map className="w-4 h-4 text-emerald-400" />
                    <span>Code Minimap</span>
                  </div>
                  {showMinimap && (
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 stroke-[2.5]" />
                  )}
                </button>

                <div
                  className="my-1 divider-line"
                  style={{ height: "1px", backgroundColor: "rgba(255, 255, 255, 0.08)", border: "none" }}
                />

                <button
                  type="button"
                  onClick={() => {
                    toggleFullscreen();
                  }}
                  className="w-full text-left px-2.5 py-2 rounded-lg flex items-center justify-between text-xs text-slate-200 hover:bg-slate-800 hover:text-white transition cursor-pointer"
                  style={{ border: "none", outline: "none" }}
                >
                  <div className="flex items-center gap-2">
                    <AiOutlineFullscreen className="w-4 h-4 text-amber-400" />
                    <span>Full Screen Workspace</span>
                  </div>
                  {isFullscreen && (
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 stroke-[2.5]" />
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Editor Feature Settings Dropdown Button */}
          <div ref={settingsMenuRef} className="relative">
            <button
              type="button"
              onClick={() => setIsSettingsMenuOpen((prev) => !prev)}
              title="Editor Feature Settings"
              className={`w-8 h-8 rounded-lg text-xs transition cursor-pointer flex items-center justify-center border-0 shadow-xs ${
                isSettingsMenuOpen
                  ? "bg-purple-600 text-white"
                  : "bg-[#161b22]/90 hover:bg-slate-800 text-slate-300 hover:text-purple-300"
              }`}
            >
              <Settings className="w-4.5 h-4.5" />
            </button>

            {isSettingsMenuOpen && (
              <div
                className="absolute right-0 top-full mt-1.5 w-60 bg-[#161b22] rounded-xl p-1.5 z-50 space-y-1 code-workspace-dark-menu"
                style={{
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  outline: "none",
                  boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.8), 0 8px 10px -6px rgba(0, 0, 0, 0.8)",
                }}
              >
                <div className="px-2.5 py-1 text-[11px] font-bold text-slate-300 select-none">
                  Editor Settings
                </div>
                <div
                  className="my-1 divider-line"
                  style={{ height: "1px", backgroundColor: "rgba(255, 255, 255, 0.08)", border: "none" }}
                />

                <button
                  type="button"
                  onClick={() => setEnableSuggestions((prev) => !prev)}
                  className="w-full text-left px-2.5 py-2 rounded-lg flex items-center justify-between text-xs text-slate-200 hover:bg-slate-800 hover:text-white transition cursor-pointer"
                  style={{ border: "none", outline: "none" }}
                >
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-amber-400" />
                    <span>Auto-Suggestions</span>
                  </div>
                  {enableSuggestions && (
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 stroke-[2.5]" />
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setEnableParameterHints((prev) => !prev)}
                  className="w-full text-left px-2.5 py-2 rounded-lg flex items-center justify-between text-xs text-slate-200 hover:bg-slate-800 hover:text-white transition cursor-pointer"
                  style={{ border: "none", outline: "none" }}
                >
                  <div className="flex items-center gap-2">
                    <HelpCircle className="w-4 h-4 text-sky-400" />
                    <span>Parameter Hints</span>
                  </div>
                  {enableParameterHints && (
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 stroke-[2.5]" />
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setEnableAutoCloseBrackets((prev) => !prev)}
                  className="w-full text-left px-2.5 py-2 rounded-lg flex items-center justify-between text-xs text-slate-200 hover:bg-slate-800 hover:text-white transition cursor-pointer"
                  style={{ border: "none", outline: "none" }}
                >
                  <div className="flex items-center gap-2">
                    <Brackets className="w-4 h-4 text-purple-400" />
                    <span>Auto-Close Brackets</span>
                  </div>
                  {enableAutoCloseBrackets && (
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 stroke-[2.5]" />
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setEnableAutoFormat((prev) => !prev)}
                  className="w-full text-left px-2.5 py-2 rounded-lg flex items-center justify-between text-xs text-slate-200 hover:bg-slate-800 hover:text-white transition cursor-pointer"
                  style={{ border: "none", outline: "none" }}
                >
                  <div className="flex items-center gap-2">
                    <Wand2 className="w-4 h-4 text-emerald-400" />
                    <span>Format on Type</span>
                  </div>
                  {enableAutoFormat && (
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 stroke-[2.5]" />
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setEnableBracketPairColorization((prev) => !prev)}
                  className="w-full text-left px-2.5 py-2 rounded-lg flex items-center justify-between text-xs text-slate-200 hover:bg-slate-800 hover:text-white transition cursor-pointer"
                  style={{ border: "none", outline: "none" }}
                >
                  <div className="flex items-center gap-2">
                    <Palette className="w-4 h-4 text-pink-400" />
                    <span>Bracket Colorization</span>
                  </div>
                  {enableBracketPairColorization && (
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 stroke-[2.5]" />
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setEnableWordWrap((prev) => !prev)}
                  className="w-full text-left px-2.5 py-2 rounded-lg flex items-center justify-between text-xs text-slate-200 hover:bg-slate-800 hover:text-white transition cursor-pointer"
                  style={{ border: "none", outline: "none" }}
                >
                  <div className="flex items-center gap-2">
                    <AlignLeft className="w-4 h-4 text-indigo-400" />
                    <span>Word Wrap</span>
                  </div>
                  {enableWordWrap && (
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 stroke-[2.5]" />
                  )}
                </button>
              </div>
            )}
          </div>

          {mode !== "free_play" && (
            <span className="bg-amber-500/20 text-amber-300 border border-amber-500/40 text-[10px] font-extrabold px-2.5 py-1 rounded-md uppercase tracking-wider">
              {mode === "competition" ? "🏆 Competition" : "🎯 Practice"}
            </span>
          )}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 3-COLUMN WORKSPACE: FILE MANAGER, MONACO EDITOR + TABS, CONSOLE OUTPUT      */}
      {/* ========================================================================= */}
      <div ref={containerRef} className="flex-1 flex overflow-hidden">
        
        {/* Column 1: Project File Manager Sidebar */}
        {showFileExplorer && (
          <ProjectFileManager
            files={projectFiles}
            activeFileId={activeFile.id}
            onSelectFile={handleSelectFile}
            onCreateFile={handleCreateFile}
            onDeleteFile={handleDeleteFile}
            onRenameFile={handleRenameFile}
            onDownloadFile={handleDownloadSingleFile}
            installedPackages={installedPackages}
            onInstallPackage={handleInstallPackage}
            isInstallingPackage={isInstallingPackage}
            width={fileExplorerWidth}
            onResizeMouseDown={handleExplorerMouseDown}
            theme="dark"
            defaultLanguage={selectedLanguage}
            canModifyFiles={!readOnly}
          />
        )}

        {/* Column 2: Code Editor Center Area with Interactive File Tabs Strip */}
        <div
          className="flex flex-col bg-[#1e1e1e] min-w-0"
          style={{
            flex: showConsolePanel ? "1 1 0%" : "1 1 100%",
            width: showConsolePanel ? `calc(100% - ${consolePanelWidth}px)` : "100%",
          }}
        >
          {/* Column 2 Sub-Header: Interactive File Tabs Strip (Aligned h-9 height) */}
          <div className="h-9 bg-[#1f242c] border-b border-[#30363d] flex items-center justify-between px-2 shrink-0 select-none">
            <div className="flex items-center gap-1 overflow-x-auto no-scrollbar h-full py-1">
              {openFiles.map((file) => {
                const isActive = file.id === activeFile.id;
                return (
                  <div
                    key={file.id}
                    onClick={() => setActiveFileId(file.id)}
                    className={`h-full flex items-center gap-2 px-3 rounded-md text-xs cursor-pointer transition select-none ${
                      isActive
                        ? "bg-[#181818] text-white font-bold border-b-2 border-purple-500 shadow-sm"
                        : "text-slate-400 hover:text-slate-200 hover:bg-[#161b22]"
                    }`}
                  >
                    {getTabFileIcon(file)}
                    <span className="text-xs truncate max-w-[140px]">{file.name}</span>
                    {!file.isEntry && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCloseFileTab(file.id);
                        }}
                        className="p-0.5 hover:bg-slate-700 rounded text-slate-400 hover:text-white transition cursor-pointer"
                        title={`Close tab (${file.name})`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                );
              })}

              {/* Quick Add File '+' Button in Tabs */}
              <button
                type="button"
                onClick={handleQuickAddFile}
                className="h-full px-2 text-slate-400 hover:text-white hover:bg-[#161b22] rounded-md transition cursor-pointer flex items-center gap-1 text-xs font-semibold"
                title={`Add new ${isPython ? "Python" : "JavaScript"} file`}
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="flex items-center gap-2 shrink-0 pl-2">
              <span className="text-[11px] text-amber-300 font-mono font-semibold">
                {activeFile.name}
              </span>
            </div>
          </div>

          {/* Monaco Editor */}
          <div className="flex-1 w-full relative">
            <Editor
              height="100%"
              path={`${selectedLanguage}/${activeFile.id}/${activeFile.name}`}
              defaultLanguage={selectedLanguage}
              language={activeFile.name.endsWith(".js") ? "javascript" : "python"}
              theme="vs-dark"
              value={activeFile.content}
              saveViewState={true}
              onChange={handleActiveFileCodeChange}
              onMount={(editor, monaco) => {
                editorRef.current = editor;
                monacoRef.current = monaco;
                if (monaco) {
                  // Setup Full Language Services for Python & JavaScript
                  setupPythonLanguageService(monaco);
                  setupJavascriptLanguageService(monaco);
                  updatePythonProjectSymbols(projectFiles);
                  updateJsProjectSymbols(monaco, projectFiles);

                  // Ctrl+S / Cmd+S save project shortcut
                  editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
                    window.dispatchEvent(new CustomEvent("workspace-save-shortcut"));
                  });

                  // Right-click Context Menu: Save to Backpack
                  editor.addAction({
                    id: "save-to-backpack-snippet",
                    label: "Save as Snippet to Backpack",
                    contextMenuGroupId: "9_cutcopypaste",
                    contextMenuOrder: 1.5,
                    keybindings: [
                      monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyB,
                    ],
                    run: () => {
                      handleSaveCodeSnippet();
                    },
                  });
                }
              }}
              options={{
                fontSize: editorFontSize,
                minimap: { enabled: showMinimap },
                quickSuggestions: enableSuggestions ? { other: true, comments: false, strings: true } : false,
                suggestOnTriggerCharacters: enableSuggestions,
                parameterHints: { enabled: enableParameterHints },
                autoClosingBrackets: enableAutoCloseBrackets ? "always" : "never",
                autoClosingQuotes: enableAutoCloseBrackets ? "always" : "never",
                formatOnType: enableAutoFormat,
                bracketPairColorization: { enabled: enableBracketPairColorization },
                wordWrap: enableWordWrap ? "on" : "off",
                scrollBeyondLastLine: false,
                automaticLayout: true,
                tabSize: 4,
                readOnly: readOnly,
              }}
            />
          </div>
        </div>

        {/* Resizer Divider between Editor and Console */}
        {showConsolePanel && (
          <div
            onMouseDown={handleConsoleMouseDown}
            className="w-1.5 hover:w-2 bg-[#30363d] hover:bg-purple-500 active:bg-purple-600 cursor-col-resize select-none transition-all shrink-0 z-20 flex items-center justify-center group"
            title="Drag to resize console output (Min: 220px)"
          >
            <div className="w-0.5 h-6 bg-slate-600 group-hover:bg-white rounded-full transition-colors" />
          </div>
        )}

        {/* Column 3: Console Output Panel */}
        <div
          className={`${showConsolePanel ? "flex" : "hidden"} flex-col bg-[#181818] shrink-0 min-w-0`}
          style={{ width: `${consolePanelWidth}px` }}
        >
          {/* Column 3 Sub-Header: Console Output Header (Aligned h-9 height) */}
          <div className="h-9 bg-[#1f242c] border-b border-[#30363d] px-3 flex items-center justify-between text-xs shrink-0 select-none">
            <div className="flex items-center gap-2 shrink-0">
              <Terminal className="w-3.5 h-3.5 text-indigo-400" />
              <span className="text-white font-extrabold text-xs">Console Output</span>
            </div>

            <div className="flex items-center gap-2.5 min-w-0">
              <div className="flex items-center gap-1.5 text-xs truncate">
                <span
                  style={{ backgroundColor: statusColor.dotColor }}
                  className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${statusColor.dotAnimation}`}
                />
                <span
                  style={{ color: statusColor.textColor }}
                  className="font-extrabold text-xs truncate"
                >
                  {status}
                </span>
              </div>

              {executionTime !== null && executionTime !== undefined && (
                <span className="text-[10px] font-mono font-bold text-slate-400 bg-slate-800/80 px-1.5 py-0.5 rounded">
                  {typeof executionTime === "number"
                    ? executionTime >= 1000
                      ? `${(executionTime / 1000).toFixed(2)}s`
                      : `${executionTime}ms`
                    : executionTime}
                </span>
              )}

              <button
                type="button"
                onClick={handleClearTerminal}
                className="p-1 text-slate-400 hover:text-red-400 hover:bg-slate-700/40 rounded transition cursor-pointer"
                title="Clear Console"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          <div
            className="flex-1 p-4 overflow-y-auto font-mono space-y-2 bg-[#181818] text-white select-text transition-all"
            style={{ fontSize: `${editorFontSize}px` }}
            onClick={() => {
              if (terminalInputRef.current) {
                terminalInputRef.current.focus();
              }
            }}
          >
            {terminalLogs.length === 0 && !activeInputRequest ? (
              <div className="h-full flex flex-col items-center justify-center text-slate-400 space-y-2 select-none">
                <Terminal className="w-10 h-10 stroke-1 opacity-40 text-slate-400" />
                <p className="text-xs font-semibold text-slate-300">Click "Run Code" to view program output here.</p>
              </div>
            ) : (
              terminalLogs.map((log, index) =>
                log.type === "error" ? (
                  <div
                    key={index}
                    className="leading-snug text-white font-normal select-text py-0.5"
                  >
                    <FormattedTerminalError text={log.text} />
                  </div>
                ) : (
                  <div
                    key={index}
                    className={`leading-relaxed whitespace-pre-wrap ${
                      log.type === "stderr"
                        ? "text-amber-300 font-medium"
                        : log.type === "system"
                        ? "text-sky-300 font-bold border-b border-[#333333] pb-1 pt-1 text-[11px]"
                        : "text-white font-medium font-mono text-xs"
                    }`}
                  >
                    {log.text}
                  </div>
                )
              )
            )}

            {/* Active Interactive Input Row (PictoBlox Inline UX) */}
            {activeInputRequest && (
              <div className="flex items-center text-xs font-mono leading-relaxed pt-0.5 min-w-0">
                {activeInputRequest.prompt && (
                  <span className="text-white whitespace-pre-wrap shrink-0 select-text">
                    {activeInputRequest.prompt}
                  </span>
                )}
                <input
                  ref={terminalInputRef}
                  type="text"
                  value={activeInputValue}
                  onChange={(e) => setActiveInputValue(e.target.value)}
                  onKeyDown={handleTerminalInputKeyDown}
                  className="flex-1 min-w-[60px] bg-transparent border-none outline-none text-emerald-400 font-mono text-xs px-1 py-0 m-0 caret-white focus:outline-none"
                  autoFocus
                  spellCheck={false}
                  autoComplete="off"
                />
              </div>
            )}

            <div ref={terminalEndRef} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PythonWorkspace;
